import { ArrowUpRight, Image, ImagePlus, Link, Link2, ListChecks, type LucideIcon, Maximize2, Minus, Pin, Plus, SquareCheck, StickyNote, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NOTE_COLORS, newId, type BoardCard, type CheckItem } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { imageUrl } from "../lib/images.ts";
import { useApp } from "../state/store.ts";
import "./board.css";

/** Pinboard of one plan object: a pan/zoom canvas with notes, checklists, images and link cards. */
export function BoardPanel() {
  const objectId = useApp((s) => s.boardFor);
  const obj = useApp((s) => s.plans.find((p) => p.id === s.boardFor));
  const tick = useApp((s) => s.boardTick);
  const cards = useMemo(() => (objectId ? controller.store?.listCards(objectId) ?? [] : []), [objectId, tick]);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [drop, setDrop] = useState(false);
  const canvas = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [linkInput, setLinkInput] = useState<string | null>(null);

  // object deleted → close
  useEffect(() => { if (objectId && !obj) controller.openBoard(null); }, [objectId, obj]);
  // opening a board: show all its cards
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (!objectId || fitted.current === objectId) return;
    fitted.current = objectId;
    setView({ x: 0, y: 0, k: 1 });
    const t = setTimeout(() => fitRef.current(), 0);
    return () => clearTimeout(t);
  }, [objectId]);
  const fitRef = useRef<() => void>(() => {});

  /** board coordinates of the visible centre (new cards go there when pasted) */
  const centre = () => {
    const r = canvas.current?.getBoundingClientRect();
    if (!r) return undefined;
    return { x: Math.round((r.width / 2 - view.x) / view.k - 110), y: Math.round((r.height / 2 - view.y) / view.k - 80) };
  };

  // paste anywhere while the board is open (not while typing in a card)
  useEffect(() => {
    if (!objectId) return;
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!e.clipboardData) return;
      e.preventDefault();
      void controller.pasteIntoBoard(objectId, e.clipboardData, centre());
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  if (!objectId || !obj) return null;

  // ---- pan & zoom the canvas ----
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.target !== canvas.current && !(e.target as HTMLElement).classList.contains("board-inner")) return;
    const start = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => setView((v) => ({ ...v, x: start.vx + ev.clientX - start.x, y: start.vy + ev.clientY - start.y }));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onWheel = (e: React.WheelEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("textarea, .card-scroll")) return;
    const r = canvas.current!.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setView((v) => {
      const k = Math.min(2.5, Math.max(0.3, v.k * Math.exp(-e.deltaY * 0.0015)));
      return { k, x: mx - ((mx - v.x) / v.k) * k, y: my - ((my - v.y) / v.k) * k };
    });
  };
  const fit = () => {
    const r = canvas.current?.getBoundingClientRect();
    if (!r || !cards.length) return setView({ x: 0, y: 0, k: 1 });
    // only zoom out when needed; never blow a few cards up
    const x0 = Math.min(...cards.map((c) => c.x)), y0 = Math.min(...cards.map((c) => c.y));
    const x1 = Math.max(...cards.map((c) => c.x + c.w)), y1 = Math.max(...cards.map((c) => c.y + c.h));
    const k = Math.min(1, Math.max(0.3, Math.min((r.width - 40) / (x1 - x0), (r.height - 40) / (y1 - y0))));
    setView({ k, x: 20 - x0 * k + ((r.width - 40) - (x1 - x0) * k) / 2, y: 20 - y0 * k });
  };

  fitRef.current = fit;

  const add = (type: "note" | "check") => { const at = centre(); controller.addCard(objectId, type, at ?? {}); };

  const linkOk = linkInput !== null && /^https?:\/\//i.test(linkInput.trim());
  return (
    <aside className="board-panel board-sheet glass-2" aria-label={`Pinboard of ${obj.name}`}>
      <div className="board-head">
        <span className="board-title">
          <span className="board-title-icon"><Pin size={14} aria-hidden="true" /></span>
          <span className="board-title-text">
            <b title={obj.name}>{obj.name}</b>
            <span className="caption">Pinboard · {cards.length ? `${cards.length} card${cards.length === 1 ? "" : "s"}` : "empty"}</span>
          </span>
        </span>
        <span className="board-tools" role="toolbar" aria-label="Add to the board">
          <button type="button" className="btn btn-icon" onClick={() => add("note")} title="Add note" aria-label="Add note"><StickyNote size={16} aria-hidden="true" /></button>
          <button type="button" className="btn btn-icon" onClick={() => add("check")} title="Add checklist" aria-label="Add checklist"><ListChecks size={16} aria-hidden="true" /></button>
          <button type="button" className="btn btn-icon" aria-pressed={linkInput !== null} onClick={() => setLinkInput(linkInput === null ? "" : null)} title="Add link · Instagram, Pinterest, YouTube or any website" aria-label="Add link"><Link2 size={16} aria-hidden="true" /></button>
          <button type="button" className="btn btn-icon" onClick={() => fileInput.current?.click()} title="Add images · or paste or drop them" aria-label="Add image"><ImagePlus size={16} aria-hidden="true" /></button>
        </span>
        <span className="board-divider" aria-hidden="true" />
        <span className="board-zoom" role="group" aria-label="Zoom">
          <button type="button" className="btn btn-icon btn-sm" onClick={() => setView((v) => ({ ...v, k: Math.max(0.3, v.k / 1.25) }))} title="Zoom out" aria-label="Zoom out"><Minus size={14} aria-hidden="true" /></button>
          <button type="button" className="btn btn-sm board-zoom-k tabular" onClick={fit} title="Fit all cards" aria-label={`Zoom ${Math.round(view.k * 100)}% · fit all cards`}>{Math.round(view.k * 100)}%</button>
          <button type="button" className="btn btn-icon btn-sm" onClick={() => setView((v) => ({ ...v, k: Math.min(2.5, v.k * 1.25) }))} title="Zoom in" aria-label="Zoom in"><Plus size={14} aria-hidden="true" /></button>
          <button type="button" className="btn btn-icon btn-sm" onClick={fit} title="Fit all cards" aria-label="Fit all cards"><Maximize2 size={14} aria-hidden="true" /></button>
        </span>
        <button type="button" className="btn btn-icon board-close" onClick={() => controller.openBoard(null)} title="Close · Esc" aria-label="Close"><X size={18} aria-hidden="true" /></button>
        <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => { const f = [...(e.target.files ?? [])]; if (f.length) void controller.addImages(objectId, f, centre()); e.target.value = ""; }} />
      </div>
      {linkInput !== null && (
        <form className="board-link-form" onSubmit={(e) => { e.preventDefault(); const u = linkInput.trim(); if (/^https?:\/\//i.test(u)) { void controller.addLink(objectId, u, centre()); setLinkInput(null); } }}>
          <label className="field-box"><Link size={16} aria-hidden="true" /><input autoFocus placeholder="https://www.pinterest.com/pin/… · instagram.com/p/… · youtube.com/…" value={linkInput} onChange={(e) => setLinkInput(e.target.value)} aria-label="Link" onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setLinkInput(null); } }} /></label>
          <button type="submit" className="btn btn-md btn-primary" disabled={!linkOk}>Add link</button>
          <button type="button" className="btn btn-md btn-ghost" onClick={() => setLinkInput(null)}>Cancel</button>
        </form>
      )}
      <div
        ref={canvas}
        className={`board-canvas${drop ? " drop" : ""}`}
        tabIndex={0}
        style={{ backgroundPosition: `${view.x}px ${view.y}px`, backgroundSize: `${22 * view.k}px ${22 * view.k}px` }}
        onPointerDown={onPointerDown}
        onWheel={onWheel}
        onDragOver={(e) => { e.preventDefault(); setDrop(true); }}
        onDragLeave={() => setDrop(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrop(false);
          const r = canvas.current!.getBoundingClientRect();
          void controller.pasteIntoBoard(objectId, e.dataTransfer, { x: Math.round((e.clientX - r.left - view.x) / view.k), y: Math.round((e.clientY - r.top - view.y) / view.k) });
        }}
      >
        <div className="board-inner" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
          {cards.map((c) => <Card key={c.id} c={c} k={view.k} onOpenImage={setLightbox} />)}
        </div>
        {cards.length === 0 && (
          <div className="board-empty">
            <span className="board-empty-icon"><Pin size={20} aria-hidden="true" /></span>
            <b>This board is empty</b>
            <span>Paste screenshots or links from Instagram, Pinterest or YouTube <kbd>Ctrl</kbd> <kbd>V</kbd></span>
            <span>Drop image files here, or add a note or a checklist above.</span>
          </div>
        )}
      </div>
      {/* in <body>: the sheet's backdrop-filter would trap a fixed overlay inside it */}
      {lightbox && createPortal(<div className="modal-back" onClick={() => setLightbox(null)}><img className="lightbox" src={lightbox} alt="" /></div>, document.body)}
    </aside>
  );
}

const ICON: Record<BoardCard["type"], LucideIcon> = { note: StickyNote, check: SquareCheck, image: Image, link: Link };

function Card({ c, k, onOpenImage }: { c: BoardCard; k: number; onOpenImage: (url: string) => void }) {
  const store = controller.store!;
  const [pos, setPos] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const p = pos ?? c;
  const CardIcon = ICON[c.type];

  /** drag (move) or resize with the pointer; saved once when released */
  const gesture = (e: React.PointerEvent, mode: "move" | "size") => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    store.raiseCard(c.id);
    const s = { x: e.clientX, y: e.clientY, c: { x: c.x, y: c.y, w: c.w, h: c.h } };
    let last = s.c;
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - s.x) / k, dy = (ev.clientY - s.y) / k;
      last = mode === "move" ? { ...s.c, x: Math.round(s.c.x + dx), y: Math.round(s.c.y + dy) } : { ...s.c, w: Math.max(140, Math.round(s.c.w + dx)), h: Math.max(90, Math.round(s.c.h + dy)) };
      setPos(last);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      store.undo.stopCapturing();
      store.updateCard(c.id, last);
      setPos(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const bg = c.type === "note" ? c.color ?? NOTE_COLORS[0] : undefined;
  return (
    <div className={`card card-${c.type}`} style={{ left: p.x, top: p.y, width: p.w, height: p.h, zIndex: c.z, background: bg }} data-card={c.id}>
      <div className="card-head" onPointerDown={(e) => gesture(e, "move")}>
        <span className="card-icon"><CardIcon size={13} aria-hidden="true" /></span>
        <span className="card-author">{c.author}</span>
        {c.type === "note" && (
          <span className="card-colors" role="radiogroup" aria-label="Note colour">
            {NOTE_COLORS.map((col) => (
              <button key={col} type="button" role="radio" aria-checked={(c.color ?? NOTE_COLORS[0]) === col} className="card-color" style={{ background: col }} onPointerDown={(e) => e.stopPropagation()} onClick={() => store.updateCard(c.id, { color: col })} title="Colour" aria-label={`Colour ${col}`} />
            ))}
          </span>
        )}
        <button type="button" className="card-del" onPointerDown={(e) => e.stopPropagation()} onClick={() => { store.undo.stopCapturing(); store.removeCard(c.id); }} title="Remove · Ctrl Z brings it back" aria-label="Remove card"><X size={14} aria-hidden="true" /></button>
      </div>
      <div className="card-body">
        {c.type === "note" && <TextField value={c.text ?? ""} placeholder="Write something…" onCommit={(text) => store.updateCard(c.id, { text })} />}
        {c.type === "check" && <Checklist c={c} />}
        {c.type === "image" && c.image && <ImageBody c={c} onOpen={onOpenImage} />}
        {c.type === "link" && c.link && <LinkBody c={c} />}
      </div>
      <div className="card-resize" onPointerDown={(e) => gesture(e, "size")} title="Resize" />
    </div>
  );
}

/** Text area that keeps local edits while focused and saves (debounced + on blur). */
function TextField({ value, onCommit, placeholder, className }: { value: string; onCommit: (v: string) => void; placeholder?: string; className?: string }) {
  const [v, setV] = useState(value);
  const focused = useRef(false);
  const timer = useRef(0);
  useEffect(() => { if (!focused.current) setV(value); }, [value]);
  const commit = (x: string) => { clearTimeout(timer.current); if (x !== value) onCommit(x); };
  return (
    <textarea
      className={className ?? "card-text"}
      value={v}
      placeholder={placeholder}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; commit(v); }}
      onChange={(e) => { setV(e.target.value); clearTimeout(timer.current); const x = e.target.value; timer.current = window.setTimeout(() => commit(x), 600); }}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}

function Checklist({ c }: { c: BoardCard }) {
  const store = controller.store!;
  const items = c.items ?? [];
  const [draft, setDraft] = useState("");
  const save = (next: CheckItem[]) => store.updateCard(c.id, { items: next });
  const done = items.filter((i) => i.done).length;
  return (
    <div className="card-scroll" onPointerDown={(e) => e.stopPropagation()}>
      {items.length > 0 && <div className="check-progress"><span className="check-bar"><i style={{ width: `${(done / items.length) * 100}%` }} /></span><span className="check-count">{done}/{items.length}</span></div>}
      {items.map((it) => (
        <div key={it.id} className={`check-item${it.done ? " done" : ""}`}>
          <input type="checkbox" checked={it.done} onChange={(e) => save(items.map((x) => (x.id === it.id ? { ...x, done: e.target.checked } : x)))} />
          <input className="check-text" defaultValue={it.text} onBlur={(e) => { if (e.target.value !== it.text) save(items.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x))); }} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
          <button type="button" className="check-del" onClick={() => save(items.filter((x) => x.id !== it.id))} title="Remove" aria-label="Remove item"><X size={14} aria-hidden="true" /></button>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); if (!draft.trim()) return; save([...items, { id: newId(), text: draft.trim(), done: false }]); setDraft(""); }}>
        <input className="check-new" placeholder="Add an item · Enter" value={draft} onChange={(e) => setDraft(e.target.value)} />
      </form>
    </div>
  );
}

function ImageBody({ c, onOpen }: { c: BoardCard; onOpen: (u: string) => void }) {
  const [url, setUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => { let alive = true; void imageUrl(c.image!.hash, controller.imageRemote()).then((u) => { if (alive) setUrl(u); }); return () => { alive = false; }; }, [c.image!.hash]);
  return (
    <div className="card-image">
      {url === undefined && <div className="img-wait">Loading…</div>}
      {url === null && <div className="img-wait">Not here yet · it appears once the person who added it is online in this world.</div>}
      {url && <img src={url} alt={c.image!.name ?? ""} draggable={false} onDoubleClick={() => onOpen(url)} title="Double-click to enlarge" />}
      <TextField className="card-caption" value={c.text ?? ""} placeholder="Add a caption…" onCommit={(text) => controller.store!.updateCard(c.id, { text })} />
    </div>
  );
}

function LinkBody({ c }: { c: BoardCard }) {
  const loading = useApp((s) => s.unfurling.includes(c.id));
  const l = c.link!;
  const host = (() => { try { return new URL(l.url).hostname.replace(/^www\./, ""); } catch { return l.url; } })();
  return (
    <div className="card-link card-scroll" onPointerDown={(e) => e.stopPropagation()}>
      {l.image && <img src={l.image} alt="" draggable={false} />}
      <a href={l.url} target="_blank" rel="noopener noreferrer" className="link-title">{l.title ?? host}<ArrowUpRight className="trail" size="1em" aria-hidden="true" /></a>
      {l.description && <p className="link-desc">{l.description}</p>}
      <span className="link-site">{l.site ?? host}</span>
      {loading && <span className="link-site">Loading preview…</span>}
      {!loading && l.blocked && !l.image && (
        <p className="warn">{/instagram/i.test(host) ? "Instagram doesn't allow previews." : "No preview for this link."} Tip: paste a screenshot of it as an image.{" "}
          <button type="button" className="link" onClick={() => void controller.refreshLink(c.id)}>Try again</button></p>
      )}
    </div>
  );
}
